import { useState, useEffect, useCallback } from 'react';
import { fmtMoney, fmtMoneyShort, fmtPct } from '@/lib/format';
import { MetricCard, LoadError } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { TrendingUp, Award, Target, Percent, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  Inglés británico (05/11/2026). Los meses de la gráfica llegan del servidor en español («Ene», «Abr»…):
  en inglés se muestran con su abreviatura inglesa.
*/
const MONTHS_EN = { Ene: 'Jan', Feb: 'Feb', Mar: 'Mar', Abr: 'Apr', May: 'May', Jun: 'Jun', Jul: 'Jul', Ago: 'Aug', Sep: 'Sep', Oct: 'Oct', Nov: 'Nov', Dic: 'Dec' };
const T = {
  es: {
    loading: 'Cargando tus comisiones…',
    loadError: undefined,
    levels: [
      ['Nivel 1 · Directa', 'Membresías que vendes tú'],
      ['Nivel 2', 'Membresías que venden los brokers que incorporaste'],
      ['Nivel 3', 'Membresías que venden los brokers que incorporaron ellos'],
    ],
    paid: 'Comisiones pagadas', paidSub: 'Total acumulado',
    pending: 'Pendiente de cobro', pendingSub: (v) => `${v} ya exigibles`,
    direct: 'Ventas directas', directSub: (p) => `N1 · ${p} sobre membresías`, directSubNoRates: 'Con tu enlace',
    network: 'Ventas de la red', networkSub: 'Niveles 2 y 3',
    monthly: 'Comisiones mensuales',
    month: (m) => m,
    referrer: (p) => `Como socio, cobras un ${p} del primer pago de cada persona que se hace miembro con tu enlace de invitación.`,
    noRates: 'Tus porcentajes aparecerán cuando tu ficha esté enlazada.',
    contributions: 'Aportaciones:',
    rules: (days) => <>Tarifa vigente desde el 20/09/2026; las ventas anteriores conservan 10 % · 5 % · 2,5 %. Ninguna venta supera el 30 % en comisiones.
          Cada comisión es exigible {days} días después de que el cliente pague el 100 % y firme los T&C, y se paga en la divisa en la que pagó el cliente.</>,
  },
  en: {
    loading: 'Loading your commissions…',
    loadError: 'We couldn’t load your commissions.',
    levels: [
      ['Level 1 · Direct', 'Memberships you sell yourself'],
      ['Level 2', 'Memberships sold by the brokers you brought in'],
      ['Level 3', 'Memberships sold by the brokers they brought in'],
    ],
    paid: 'Commissions paid', paidSub: 'All-time total',
    pending: 'Awaiting payment', pendingSub: (v) => `${v} payable now`,
    direct: 'Direct sales', directSub: (p) => `L1 · ${p} on memberships`, directSubNoRates: 'Through your link',
    network: 'Network sales', networkSub: 'Levels 2 and 3',
    monthly: 'Monthly commissions',
    month: (m) => MONTHS_EN[m] || m,
    referrer: (p) => `As a member, you receive ${p} of the first payment from everyone who becomes a member through your invitation link.`,
    noRates: 'Your rates will appear once your record is linked.',
    contributions: 'Contributions:',
    rules: (days) => <>Current rates apply from 20/09/2026; earlier sales keep 10% · 5% · 2.5%. Commissions on any single sale never exceed 30% in total.
          Each commission becomes payable {days} days after the client has paid in full and signed the T&amp;Cs, and is paid in the currency the client paid in.</>,
  },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0d1829] border border-white/[0.08] rounded-xl px-4 py-3 shadow-2xl">
      <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white font-bold">{fmtMoney(payload[0].value, 'eur')}</p>
    </div>
  );
};

/*
  Comisiones › Resumen (antes la página «Rendimiento», 16/10/2026): totales, comisiones de cada mes y
  porcentajes por nivel. Lo ven los brokers Premium y, con su 10 % de referidor, los socios Access y PRO.
*/
export default function ComisionesResumen() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // La gráfica mensual es opcional: si /metrics falla, la página sigue con las comisiones
    Promise.all([dashboardApi.commissions(), dashboardApi.metrics().catch(() => null)])
      .then(([c, m]) => { setData(c); setMetrics(m); })
      .catch((e) => { console.error(e); setError(e.message || 'Error'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="animate-spin text-[#c9a84c]" size={28} />
        <span className="text-xs text-gray-400">{t.loading}</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="space-y-8" data-testid="comisiones-resumen">
      <LoadError onRetry={load} text={t.loadError} />
    </div>
  );

  // Datos reales del CDM: resumen de comisiones y tarifas vigentes (sin ficha enlazada no hay tarifas)
  const s = data?.summary || {};
  const r = data?.rates || null;
  const pct = (v) => fmtPct(v ?? 0);
  const totalPaid = s.paid || 0;
  const totalPending = Math.max(0, (s.accrued || 0) - (s.paid || 0));
  const directCount = s.direct_count || 0;
  const networkCount = s.network_count || 0;

  const levels = !r ? [] : [
    { label: t.levels[0][0], pct: pct(r.n1), aport: r.fhct ? pct(r.fhct.n1) : null, desc: t.levels[0][1], gradient: 'from-[#c9a84c]/20 to-[#c9a84c]/5' },
    { label: t.levels[1][0], pct: pct(r.n2), aport: r.fhct ? pct(r.fhct.n2) : null, desc: t.levels[1][1], gradient: 'from-[#c9a84c]/15 to-[#c9a84c]/3' },
    { label: t.levels[2][0], pct: pct(r.n3), aport: r.fhct ? pct(r.fhct.n3) : null, desc: t.levels[2][1], gradient: 'from-[#c9a84c]/10 to-[#c9a84c]/2' },
  ];

  return (
    <div className="space-y-8" data-testid="comisiones-resumen">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label={t.paid} value={`${fmtMoney(totalPaid, 'eur')}`} sub={t.paidSub} icon={TrendingUp} delay={0} />
        <MetricCard label={t.pending} value={`${fmtMoney(totalPending, 'eur')}`} sub={t.pendingSub(fmtMoney(s.payable || 0, 'eur'))} icon={Target} color="#f59e0b" delay={1} />
        <MetricCard label={t.direct} value={directCount} sub={r ? t.directSub(pct(r.n1)) : t.directSubNoRates} icon={Award} delay={2} />
        {r && <MetricCard label={t.network} value={networkCount} sub={t.networkSub} icon={Percent} delay={3} />}
      </div>

      {metrics?.source !== 'none' && metrics?.monthly?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
        >
          <h3 className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-5">{t.monthly}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={metrics.monthly.map((m) => ({ ...m, month: t.month(m.month) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
              <XAxis dataKey="month" tick={{ fill: '#4b5563', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#4b5563', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => fmtMoneyShort(v, 'eur')} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="comisiones" fill="#c9a84c" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      {!r && data?.referrer_pct && (
        <p className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-sm text-gray-300" data-testid="resumen-referidor">
          {t.referrer(fmtPct(data.referrer_pct))}
        </p>
      )}
      {!r && !data?.referrer_pct && (
        <p className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-sm text-gray-400" data-testid="rendimiento-sin-tarifas">
          {t.noRates}
        </p>
      )}

      {levels.length > 0 && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {levels.map((l, i) => (
          <motion.div
            key={l.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.4 + i * 0.08 }}
            className="group relative rounded-2xl border border-white/[0.06] overflow-hidden hover:border-[#c9a84c]/20 transition-colors duration-300"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${l.gradient}`} />
            <div className="relative p-6">
              <p className="text-[#c9a84c] text-3xl font-bold mb-2">{l.pct}</p>
              <p className="text-white text-sm font-medium">{l.label}</p>
              <p className="text-gray-400 text-xs mt-1">{l.desc}</p>
              {l.aport && <p className="text-gray-500 text-[11px] mt-2">{t.contributions} {l.aport}</p>}
            </div>
          </motion.div>
        ))}
      </div>
      )}

      {r && (
        <p className="text-[11px] text-gray-500 leading-relaxed" data-testid="rendimiento-reglas">
          {t.rules(data?.payable_days ?? 15)}
        </p>
      )}
    </div>
  );
}
